import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import {
  Assessment as AssessmentIcon,
  Close as CloseIcon
} from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import VerificationAssignDesignOutputDialog from '../dialogs/VerificationAssignDesignOutputDialog';
import UnlinkConfirmDialog from '../dialogs/UnlinkConfirmDialog';
import {
  useFindProductDesignOutputsByVerificationId,
  useProductEntityPrefix,
  useAssignVerificationDesignOutput
} from '../Product.hooks';
import {
  ProductDesignOutput,
  BaseProductVerificationCardProps
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

const VerificationDesignOutputReferenceCard: React.FC<BaseProductVerificationCardProps> = (
  props: BaseProductVerificationCardProps
) => {
  const {
    productId,
    productVerification,
    productVerificationVersion,
    onUpdate,
    onError
  } = props;
  const history = useHistory();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [itemToUnlink, setItemToUnlink] = useState<number | undefined>();
  const [order, setOrder] = useState<string | undefined>();
  const [orderDirection, setOrderDirection] = useState<
    'ASC' | 'DESC' | undefined
  >();
  const {
    productDesignOutputList,
    isLoadingProductDesignOutputs,
    loadingProductDesignOutputsError,
    findProductDesignOutputs
  } = useFindProductDesignOutputsByVerificationId(
    productId,
    productVerification.id
  );
  const { isFieldDisabled } = useTaskEntityFields(productVerification.task);
  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.DesignOutput
  );

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const findProductDesignOutputsWithParams = useCallback(() => {
    const params: ApiParams = {};
    if (order) params.order = order;
    if (orderDirection) params.direction = orderDirection;
    if (productVerificationVersion)
      params.version = productVerificationVersion.version;
    findProductDesignOutputs(params);
  }, [
    findProductDesignOutputs,
    order,
    orderDirection,
    productVerificationVersion
  ]);

  useEffect(() => {
    findProductDesignOutputsWithParams();
  }, [findProductDesignOutputsWithParams]);

  useEffect(() => {
    if (loadingProductDesignOutputsError) {
      onError?.(loadingProductDesignOutputsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductDesignOutputsError]);
  const disabled =
    Boolean(productVerificationVersion) || isFieldDisabled('designOutput');
  return (
    <Card>
      <CardTitle>
        <AssessmentIcon />
        {getProductEntityName('designOutput')} Reference
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignOutput.id'}
                  direction={getSortDirection('productDesignOutput.id')}
                  onClick={() => handleSort('productDesignOutput.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignOutput.title'}
                  direction={getSortDirection('productDesignOutput.title')}
                  onClick={() => handleSort('productDesignOutput.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductDesignOutputs && (
              <TableRow>
                <TableCell colSpan={2} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productDesignOutputList?.map(
              (designOutput: ProductDesignOutput) => (
                <TableRow
                  hover
                  key={designOutput.id}
                  onClick={() => {
                    history.push(
                      `/portal/products/${productId}/design-outputs/${designOutput.id}`
                    );
                  }}
                >
                  <TableCell>{prefix + designOutput.id}</TableCell>
                  <WrappedTableCell>{designOutput.title}</WrappedTableCell>
                  <TableCell>
                    <TaskStatusLabel task={designOutput.task} />
                  </TableCell>
                  <TableCell
                    style={{
                      textAlign: 'center'
                    }}
                  >
                    {!disabled && (
                      <CloseIcon
                        onClick={event => {
                          event.stopPropagation();
                          setItemToUnlink(designOutput.id);
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              )
            )}
            <TableRow>
              <TableCell colSpan={2}>
                <Button
                  variant='outlined'
                  color='primary'
                  disabled={disabled}
                  onClick={() => setOpenDialog(true)}
                >
                  Assign {getProductEntityName('designOutput')}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {openDialog && (
        <VerificationAssignDesignOutputDialog
          open={openDialog}
          productId={productId}
          verificationId={productVerification.id}
          selectedItems={productDesignOutputList}
          onError={onError}
          onCancel={() => setOpenDialog(false)}
          onAssigned={() => {
            findProductDesignOutputsWithParams();
            onUpdate?.();
            setOpenDialog(false);
          }}
        />
      )}
      <UnlinkConfirmDialog
        open={!!itemToUnlink}
        productId={productId}
        entityId={productVerification.id}
        from={ProductBoardColumnEntity.Verification}
        to={ProductBoardColumnEntity.DesignOutput}
        unlinkItemId={itemToUnlink}
        selectedItems={productDesignOutputList}
        onError={onError}
        onCancel={() => setItemToUnlink(undefined)}
        onChange={() => {
          findProductDesignOutputsWithParams();
          onUpdate?.();
          setItemToUnlink(undefined);
        }}
      />
    </Card>
  );
};

export default VerificationDesignOutputReferenceCard;
